import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Tooltip,
  useMediaQuery,
  Collapse,
  ListSubheader
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Computer as ComputerIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  QueuePlayNext as QueueIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  BarChart as BarChartIcon,
  ListAlt as ListIcon,
  Collections as CollectionsIcon,
  History as HistoryIcon,
  PlayArrow as ProcessSettingsIcon,
  Speed as TestIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Info as InfoIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

import { useKeycloak } from '../context/KeycloakContext';
import queueService from '../services/queueService';
import { useAuth } from '../context/AuthContext';
import env from '../config/env';
import RoleService from '../services/RoleService';
import Changelog from '../pages/Changelog';
import GlobalDropOverlay from './GlobalDropOverlay';

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerStyled = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': {
      ...openedMixin(theme),
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': {
      ...closedMixin(theme),
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
  }),
}));

const Layout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { keycloak, isAuthenticated: isKeycloakAuthenticated, logout: keycloakLogout } = useKeycloak();
  const { logout: customLogout } = useAuth();
  const useKeycloakAuth = env.useKeycloakAuth;
  const userInfo = keycloak.tokenParsed || {};
  const isAdmin = useKeycloakAuth ? userInfo.groups?.some(group => group.toLowerCase().includes('admin')) : true; // For custom auth, we'll assume admin for now
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(!isMobile);
  const [queues, setQueues] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [changelogOpen, setChangelogOpen] = useState(false);

  const userMenuOpen = Boolean(anchorEl);
  const notificationMenuOpen = Boolean(notificationAnchorEl);
  const dragCounterRef = useRef(0);
  const [showGlobalDropOverlay, setShowGlobalDropOverlay] = useState(false);

  const defaultOwner = useMemo(() => {
    if (env.useKeycloakAuth) {
      return keycloak?.tokenParsed?.preferred_username || keycloak?.tokenParsed?.email || '';
    }
    return userInfo?.preferred_username || userInfo?.email || '';
  }, [keycloak, userInfo]);

  const refreshCurrentRoute = useCallback(() => {
    navigate(0);
  }, [navigate]);

  const closeDropOverlay = useCallback(() => {
    dragCounterRef.current = 0;
    setShowGlobalDropOverlay(false);
  }, []);

  useEffect(() => {
    const isPdfDrag = (event) => {
      const dataTransfer = event?.dataTransfer;
      if (!dataTransfer) return false;

      const { types } = dataTransfer;
      if (types && typeof types.includes === 'function' && types.includes('Files')) {
        return true;
      }

      const items = dataTransfer.items;
      if (items && items.length > 0) {
        for (let i = 0; i < items.length; i += 1) {
          const item = items[i];
          if (item.kind === 'file') {
            if (!item.type || item.type === 'application/pdf') {
              return true;
            }
          }
        }
      }

      const files = dataTransfer.files;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          if (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')) {
            return true;
          }
        }
      }

      return false;
    };

    const handleDragEnter = (event) => {
      if (!isPdfDrag(event)) {
        return;
      }
      event.preventDefault();
      dragCounterRef.current += 1;
      setShowGlobalDropOverlay(true);
    };

    const handleDragOver = (event) => {
      if (!showGlobalDropOverlay && !isPdfDrag(event)) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = (event) => {
      if (!showGlobalDropOverlay) return;
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setShowGlobalDropOverlay(false);
      }
    };

    const handleWindowDrop = (event) => {
      if (!showGlobalDropOverlay) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      closeDropOverlay();
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [closeDropOverlay, showGlobalDropOverlay]);

  const loadQueueSummaries = useCallback(async (queuesList) => {
    if (!Array.isArray(queuesList) || queuesList.length === 0) {
      return;
    }

    const updatedQueues = await Promise.all(
      queuesList.map(async (queue) => {
        try {
          const [unassignedTotal] = await Promise.all([
            queueService.getQueueCount(queue.id, 'UNASSIGNED_OR_OPEN'),
          ]);

          return {
            ...queue,
            summary: {
              unassigned: unassignedTotal,
            },
          };
        } catch (summaryError) {
          console.error(`Failed to load summary for queue ${queue.id}`, summaryError);
          return queue;
        }
      })
    );

    setQueues(updatedQueues);
  }, []);

  // Carica le code disponibili per la sidebar
  React.useEffect(() => {
    let cancelled = false;

    const fetchQueues = async () => {
      try {
        const data = await queueService.getAll();
        if (cancelled) {
          return;
        }

        const queuesWithSummaries = data.map((queue) => ({
          ...queue,
          summary: {
            assigned: queue.count ?? 0,
            unassigned: 0,
          },
        }));
        setQueues(queuesWithSummaries);
        loadQueueSummaries(queuesWithSummaries);
      } catch (err) {
        console.error('Error loading queues:', err);
      }
    };

    fetchQueues();

    return () => {
      cancelled = true;
    };
  }, [loadQueueSummaries]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    if (useKeycloakAuth) {
      keycloakLogout();
    } else {
      customLogout();
      navigate('/login');
    }
  };

  // Determine if a menu item is active based on current path
  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleChangelogOpen = () => {
    setChangelogOpen(true);
  };

  const handleChangelogClose = () => {
    setChangelogOpen(false);
  };

  const mainMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Documenti', icon: <CollectionsIcon />, path: '/documenti' },
    { text: 'Cronologia', icon: <HistoryIcon />, path: '/codex-process-list' },
  ];

  // Settings submenu items
  const settingsItems = [
    { text: 'Code di lavoro', icon: <QueueIcon />, path: '/queues', configManageOnly: true },
    { text: 'Tipi di Codex', icon: <DescriptionIcon />, path: '/codex-types', configManageOnly: true },
    { text: 'Anagrafiche', icon: <FolderIcon />, path: '/registries', configAdvancedOnly: true},
    { text: 'Gestione Prompts', icon: <ChatBubbleOutlineIcon />, path: '/prompts', configAdvancedOnly: true},
    { text: 'Gestione Processi', icon: <ProcessSettingsIcon />, path: '/processes', configAdvancedOnly: true},
    { text: 'Test Validation', icon: <TestIcon />, path: '/test-validation', configAdvancedOnly: true},
    { text: 'System', icon: <SettingsIcon />, path: '/system-settings', configAdvancedOnly: true},
  ];

  const visibleSettingsItems = settingsItems.filter(item => (
    (!item.adminOnly || isAdmin) &&
    (!item.configManageOnly || RoleService.hasConfigManageRole()) &&
    (!item.configAdvancedOnly || RoleService.hasConfigAdvancedRole())
  ));

  const isSettingsRoute = visibleSettingsItems.some(item => location.pathname.startsWith(item.path));
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <GlobalDropOverlay
        open={showGlobalDropOverlay}
        onRequestClose={closeDropOverlay}
        defaultOwner={defaultOwner}
        onCompleted={refreshCurrentRoute}
      />

      {/* App Bar */}
      <AppBarStyled position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            <img src="/logo_automate.png" alt="Automate Logo" style={{ height: 32, verticalAlign: 'middle' }} />
          </Typography>


          {/*

          <IconButton color="inherit" sx={{ mr: 2 }}>
            <SearchIcon />
          </IconButton>

          <IconButton
            color="inherit"
            sx={{ mr: 2 }}
            onClick={handleNotificationMenuOpen}
          >
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          */}

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchorEl}
            open={notificationMenuOpen}
            onClose={handleNotificationMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleNotificationMenuClose}>
              New process in Processing Queue
            </MenuItem>
            <MenuItem onClick={handleNotificationMenuClose}>
              Error in process #104
            </MenuItem>
            <MenuItem onClick={handleNotificationMenuClose}>
              2 processes completed
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleNotificationMenuClose}>
              View all notifications
            </MenuItem>
          </Menu>

          {/* User menu */}
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleUserMenuOpen}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={userMenuOpen ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={userMenuOpen ? 'true' : undefined}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {keycloak.tokenParsed?.preferred_username?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* User dropdown menu */}
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBarStyled>

      {/* Sidebar */}
      <DrawerStyled variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>

        <Divider />

        {/* Main Menu Items */}
        <List>
          {mainMenuItems.map((item) => (
            (!item.configManageOnly || RoleService.hasConfigManageRole()) && (
              <ListItem
                key={item.text}
                disablePadding
                sx={{
                  display: 'block',
                  bgcolor: isActive(item.path) ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                }}
                onClick={() => navigate(item.path)}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: isActive(item.path) ? 'primary.main' : 'inherit'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      opacity: open ? 1 : 0,
                      '& .MuiTypography-root': {
                        fontWeight: isActive(item.path) ? 'bold' : 'normal'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )
          ))}
        </List>

        <Divider sx={{ my: 1.5 }} />

        {RoleService.hasQueueViewRole() && (
          <>
            {/* Queue List - Each queue has activities */}
            <ListSubheader
              sx={{
                bgcolor: 'background.paper',
                lineHeight: '30px',
                opacity: open ? 1 : 0,
                height: open ? 'auto' : 0,
                overflow: 'hidden'
              }}
            >
              CODE DI LAVORO
            </ListSubheader>

            <List sx={{
              flexGrow: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              {queues && queues.length > 0 ? (
                queues.map((queue) => (
                  <ListItem
                    key={queue.id}
                    disablePadding
                    sx={{
                      display: 'block',
                      bgcolor: isActive(`/queues/${queue.id}`) ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                    }}
                    onClick={() => navigate(`/queues/${queue.id}`)}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                          color: isActive(`/working-queues/${queue.id}`) ? 'primary.main' : 'inherit'
                        }}
                      >
                        <Badge
                          badgeContent={queue.summary?.unassigned ?? queue.count ?? 0}
                          color="warning"
                          overlap="circular"
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}
                        >
                          <FolderIcon />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: isActive(`/working-queues/${queue.id}`) ? 'bold' : 'normal',
                              maxWidth: 150
                            }}
                          >
                            {queue.name}
                          </Typography>
                        }
                        secondary={queue.code}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              ) : (
                <ListItem disablePadding sx={{ display: open ? 'block' : 'none' }}>
                  <ListItemButton disabled sx={{ opacity: 0.7, minHeight: 48 }}>
                    <ListItemText
                      primary="No queues available"
                      primaryTypographyProps={{ fontSize: '0.8rem', fontStyle: 'italic' }}
                      sx={{ opacity: open ? 0.7 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </>
        )}

        {/* Spacer to push Settings to bottom */}
        <Box sx={{ flexGrow: 1 }} />

        <Divider />

        {/* Settings Items at bottom */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Box sx={{
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(0, 0, 0, 0.3)',
            },
          }}>
            <List>
              {visibleSettingsItems.length > 0 && (
                <>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      onClick={() => setSettingsMenuOpen(prev => !prev)}
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                        bgcolor: isSettingsRoute ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                          color: isSettingsRoute ? 'primary.main' : 'inherit'
                        }}
                      >
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Settings"
                        sx={{
                          opacity: open ? 1 : 0,
                          color: isSettingsRoute ? 'primary.main' : 'inherit'
                        }}
                      />
                      {open && (settingsMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={settingsMenuOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {visibleSettingsItems.map((item) => (
                        <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                          <ListItemButton
                            sx={{
                              minHeight: 44,
                              justifyContent: open ? 'initial' : 'center',
                              px: 2.5,
                              pl: open ? 4 : 2.5,
                              bgcolor: location.pathname.startsWith(item.path) ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                            }}
                            onClick={() => navigate(item.path)}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: open ? 3 : 'auto',
                                justifyContent: 'center',
                                color: location.pathname.startsWith(item.path) ? 'primary.main' : 'inherit'
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.text}
                              sx={{
                                opacity: open ? 1 : 0,
                                color: location.pathname.startsWith(item.path) ? 'primary.main' : 'inherit'
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              )}
            </List>
          </Box>

          {/* Version display at bottom */}
          {visibleSettingsItems.length > 0 && (
            <Box sx={{
              mt: 'auto',
              borderTop: 1,
              borderColor: 'divider',
              p: 1
            }}>
              <ListItem
                disablePadding
                sx={{
                  display: 'block',
                  bgcolor: changelogOpen ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                }}
                onClick={handleChangelogOpen}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: changelogOpen ? 'primary.main' : 'text.secondary'
                    }}
                  >
                    <InfoIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${env.version}`}
                    sx={{
                      opacity: open ? 1 : 0,
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem',
                                              fontWeight: changelogOpen ? 'bold' : 'normal',
                      color: changelogOpen ? 'primary.main' : 'text.secondary'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </Box>
          )}
        </Box>
      </DrawerStyled>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          pl: 3,
          pr: 3,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <DrawerHeader />
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
          <Outlet />
        </Box>
      </Box>

      {/* Changelog Dialog */}
      <Changelog open={changelogOpen} onClose={handleChangelogClose} />
    </Box>
  );
};

export default Layout;
